import React, { useState, useEffect } from 'react';
import { X, Star, Calendar, Phone, Info, DollarSign, Clock, FileText, CheckCircle, XCircle, ChevronDown, ChevronUp } from 'lucide-react';
import { Lead } from '../types';
import { LEAD_STATUS, LEAD_STATUS_LABELS } from '../constants/leadStatuses';
import { AgentCall } from '../types/agentCall';
import { usersService, agentCallsService } from '../services/supabaseService';
import AgentCallsList from './AgentCallsList';
import AgentCallDetailModal from './AgentCallDetailModal';
import LeadSourceBadge from './LeadSourceBadge';
import LeadQualifiedBadge from './LeadQualifiedBadge';
import LeadIntelligencePanel from './LeadIntelligencePanel';
import LeadFunnelActions from './LeadFunnelActions';
import { parseLeadFormData } from '../utils/leadFormDataParser';

interface LeadModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (lead: Partial<Lead>) => void;
  lead?: Lead | null;
}

// Country codes for phone numbers
const countryCodes = [
  { code: '+34', country: 'España', flag: '🇪🇸' },
  { code: '+1', country: 'Estados Unidos', flag: '🇺🇸' },
  { code: '+44', country: 'Reino Unido', flag: '🇬🇧' },
  { code: '+33', country: 'Francia', flag: '🇫🇷' },
  { code: '+49', country: 'Alemania', flag: '🇩🇪' },
  { code: '+39', country: 'Italia', flag: '🇮🇹' },
  { code: '+351', country: 'Portugal', flag: '🇵🇹' },
  { code: '+54', country: 'Argentina', flag: '🇦🇷' },
  { code: '+52', country: 'México', flag: '🇲🇽' },
  { code: '+57', country: 'Colombia', flag: '🇨🇴' },
  { code: '+56', country: 'Chile', flag: '🇨🇱' },
  { code: '+51', country: 'Perú', flag: '🇵🇪' },
  { code: '+598', country: 'Uruguay', flag: '🇺🇾' },
  { code: '+58', country: 'Venezuela', flag: '🇻🇪' },
  { code: '+593', country: 'Ecuador', flag: '🇪🇨' },
  { code: '+55', country: 'Brasil', flag: '🇧🇷' }
];

// Product interest options
const productOptions = [
  'Desarrollo Web',
  'Desarrollo IA',
  'Desarrollo CRM',
  'Desarrollo ERP',
  'Branding y Marketing',
  'E-commerce',
  'Consultoría Digital',
  'Desarrollo Móvil',
  'SEO y Marketing Digital',
  'Automatización de Procesos'
];
const LeadModal: React.FC<LeadModalProps> = ({ isOpen, onClose, onSave, lead }) => {
  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    countryCode: '+34',
    phoneNumber: '',
    company: '',
    role: '',
    source: 'manual' as Lead['source'],
    status: LEAD_STATUS.NUEVO as Lead['status'],
    productInterest: [] as string[],
    owner: '',
    notes: '',
    score: 0,
    lastContactDate: '',
    nextFollowUp: ''
  });

  const [availableUsers, setAvailableUsers] = useState<any[]>([]);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});

  const [agentCalls, setAgentCalls] = useState<AgentCall[]>([]);
  const [agentCallsLoading, setAgentCallsLoading] = useState(false);
  const [selectedCall, setSelectedCall] = useState<AgentCall | null>(null);
  const [callModalOpen, setCallModalOpen] = useState(false);
  const [conversationFullExpanded, setConversationFullExpanded] = useState(false);

  // Parse phone number into country code and number
  const parsePhoneNumber = (phone: string) => {
    if (!phone) return { countryCode: '+34', phoneNumber: '' };
    
    // Find matching country code
    const matchingCode = countryCodes.find(cc => phone.startsWith(cc.code));
    if (matchingCode) {
      return {
        countryCode: matchingCode.code,
        phoneNumber: phone.substring(matchingCode.code.length).trim()
      };
    }
    
    // Default to Spain if no match
    return { countryCode: '+34', phoneNumber: phone };
  };

  // Parse product interest from string or array
  const parseProductInterest = (productInterest: string | string[]): string[] => {
    if (Array.isArray(productInterest)) {
      return productInterest;
    }
    
    if (!productInterest) return [];
    
    // Try to parse as JSON first
    try {
      const parsed = JSON.parse(productInterest);
      return Array.isArray(parsed) ? parsed : [productInterest];
    } catch {
      // If not JSON, treat as single string
      return [productInterest];
    }
  };
  useEffect(() => {
    if (isOpen) {
      loadUsers();
    }
  }, [isOpen]);

  useEffect(() => {
    if (!lead?.id || !isOpen) {
      setAgentCalls([]);
      return;
    }

    let cancelled = false;
    setAgentCallsLoading(true);

    agentCallsService.getByLeadId(lead.id)
      .then(calls => {
        if (cancelled) return;
        setAgentCalls(calls);
      })
      .catch(err => {
        console.error('[AGENT_CALLS] Error loading for lead', lead.id, err);
      })
      .finally(() => {
        if (!cancelled) setAgentCallsLoading(false);
      });

    return () => { cancelled = true; };
  }, [lead?.id, isOpen]);

  useEffect(() => {
    if (lead) {
      const { countryCode, phoneNumber } = parsePhoneNumber(lead.phone || '');
      const productInterestArray = parseProductInterest(lead.productInterest);
      
      setFormData({
        fullName: lead.fullName,
        email: lead.email,
        countryCode,
        phoneNumber,
        company: lead.company,
        role: lead.role || '',
        source: lead.source,
        status: lead.status,
        productInterest: productInterestArray,
        owner: lead.owner,
        notes: lead.notes,
        score: lead.score,
        lastContactDate: lead.lastContactDate
          ? (typeof lead.lastContactDate === 'string'
              ? lead.lastContactDate.split('T')[0]
              : (lead.lastContactDate as Date).toISOString().split('T')[0])
          : '',
        nextFollowUp: lead.nextFollowUp
          ? (typeof lead.nextFollowUp === 'string'
              ? lead.nextFollowUp.split('T')[0]
              : (lead.nextFollowUp as Date).toISOString().split('T')[0])
          : ''
      });
    } else {
      setFormData({
        fullName: '',
        email: '',
        countryCode: '+34',
        phoneNumber: '',
        company: '',
        role: '',
        source: 'manual',
        status: LEAD_STATUS.NUEVO,
        productInterest: [],
        owner: '',
        notes: '',
        score: 0,
        lastContactDate: '',
        nextFollowUp: ''
      });
    }
  }, [lead]);

  // Handle phone number input (only numbers)
  const handlePhoneNumberChange = (value: string) => {
    // Only allow numbers, spaces, and hyphens
    const cleanValue = value.replace(/[^\d\s-]/g, '');
    setFormData({ ...formData, phoneNumber: cleanValue });
  };

  // Handle multiple product selection
  const handleProductInterestChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const selectedOptions = Array.from(e.target.selectedOptions, option => option.value);
    setFormData({ ...formData, productInterest: selectedOptions });
  };
  const loadUsers = async () => {
    try {
      setLoadingUsers(true);
      const users = await usersService.getAll();
      const activeUsers = users.filter(user => user.isActive);
      setAvailableUsers(activeUsers);
      
      // Set default owner if no lead is being edited
      if (!lead && activeUsers.length > 0) {
        setFormData(prev => ({
          ...prev,
          owner: activeUsers[0].name
        }));
      }
    } catch (error) {
      console.error('Error loading users:', error);
      setAvailableUsers([]);
    } finally {
      setLoadingUsers(false);
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    // Validate required fields
    const newErrors: Record<string, string> = {};
    
    if (!formData.fullName.trim()) {
      newErrors.fullName = 'El nombre completo es obligatorio';
    }
    
    if (!formData.email.trim()) {
      newErrors.email = 'El email es obligatorio';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'El email no tiene un formato válido';
    }
    
    if (!formData.company.trim()) {
      newErrors.company = 'La empresa es obligatoria';
    }
    
    if (formData.productInterest.length === 0) {
      newErrors.productInterest = 'El producto de interés es obligatorio';
    }
    
    if (!formData.owner.trim()) {
      newErrors.owner = 'El propietario es obligatorio';
    }
    
    setErrors(newErrors);
    
    if (Object.keys(newErrors).length > 0) {
      return;
    }
    
    // Combine country code and phone number
    const fullPhone = formData.phoneNumber ? 
      `${formData.countryCode} ${formData.phoneNumber}` : '';
    
    const productInterestString = JSON.stringify(formData.productInterest);
    
    // Prepare the lead data for saving
    const leadData = {
      fullName: formData.fullName,
      email: formData.email,
      phone: fullPhone,
      company: formData.company,
      role: formData.role,
      source: formData.source,
      status: formData.status,
      productInterest: productInterestString,
      owner: formData.owner,
      notes: formData.notes,
      score: formData.score,
      lastContactDate: formData.lastContactDate ? new Date(formData.lastContactDate) : undefined,
      nextFollowUp: formData.nextFollowUp ? new Date(formData.nextFollowUp) : undefined,
      formData: {}
    };
    
    console.log('💾 Guardando lead:', leadData);
    onSave(leadData);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full mx-4 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <h2 className="text-xl font-semibold text-gray-900">
            {lead ? 'Editar Lead' : 'Nuevo Lead'}
          </h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* Basic Information */}
          <div>
            <h3 className="text-lg font-medium text-gray-900 mb-4">Información Básica</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Nombre Completo *
                </label>
                <input
                  type="text"
                  required
                  value={formData.fullName}
                  onChange={(e) => setFormData({ ...formData, fullName: e.target.value })}
                  className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                    errors.fullName ? 'border-red-500 bg-red-50' : 'border-gray-300'
                  }`}
                  placeholder="ej: Juan Pérez"
                />
                {errors.fullName && (
                  <p className="text-red-600 text-xs mt-1">{errors.fullName}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Email *
                </label>
                <input
                  type="email"
                  required
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                    errors.email ? 'border-red-500 bg-red-50' : 'border-gray-300'
                  }`}
                  placeholder="juan@empresa.com"
                />
                {errors.email && (
                  <p className="text-red-600 text-xs mt-1">{errors.email}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Teléfono
                </label>
                <div className="flex space-x-2">
                  <select
                    value={formData.countryCode}
                    onChange={(e) => setFormData({ ...formData, countryCode: e.target.value })}
                    className="w-32 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    {countryCodes.map(cc => (
                      <option key={cc.code} value={cc.code}>
                        {cc.flag} {cc.code}
                      </option>
                    ))}
                  </select>
                  <input
                    type="tel"
                    value={formData.phoneNumber}
                    onChange={(e) => handlePhoneNumberChange(e.target.value)}
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="666 123 456"
                  />
                </div>
                <p className="text-xs text-gray-500 mt-1">
                  Solo números, espacios y guiones permitidos
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Empresa *
                </label>
                <input
                  type="text"
                  required
                  value={formData.company}
                  onChange={(e) => setFormData({ ...formData, company: e.target.value })}
                  className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                    errors.company ? 'border-red-500 bg-red-50' : 'border-gray-300'
                  }`}
                  placeholder="ej: TechCorp SL"
                />
                {errors.company && (
                  <p className="text-red-600 text-xs mt-1">{errors.company}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Cargo/Rol
                </label>
                <input
                  type="text"
                  value={formData.role}
                  onChange={(e) => setFormData({ ...formData, role: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="ej: Director de IT"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Fuente del Lead *
                </label>
                <select
                  value={formData.source}
                  onChange={(e) => setFormData({ ...formData, source: e.target.value as Lead['source'] })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="manual">Manual</option>
                  <option value="form_web">Formulario Web</option>
                  <option value="phone_call">Llamada Telefónica</option>
                  <option value="email">Email</option>
                  <option value="referral">Referido</option>
                  <option value="social_media">Redes Sociales</option>
                  <option value="advertising">Publicidad</option>
                  <option value="shakers">Shakers</option>
                  <option value="sortlist">Sortlist</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Estado del Lead *
                </label>
                {/*
                  Al crear un lead nuevo el select es libre. Al editar un
                  existente lo desactivamos: cambiar el status debe pasar por
                  LeadFunnelActions, que llama a la RPC transition_lead
                  (inserta activity, aplica anti-retroceso y respeta orden
                  lineal). Antes el select escribía leads.status directo
                  bypasseando esas reglas y permitía "ganado → nuevo".
                */}
                <select
                  value={formData.status}
                  onChange={(e) => setFormData({ ...formData, status: e.target.value as Lead['status'] })}
                  disabled={!!lead?.id}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-gray-100 disabled:cursor-not-allowed"
                >
                  {Object.entries(LEAD_STATUS_LABELS).map(([value, label]) => (
                    <option key={value} value={value}>{label}</option>
                  ))}
                </select>
                {lead?.id && (
                  <p className="mt-1 text-xs text-gray-500">
                    Usa los botones del funnel de abajo para cambiar el estado.
                  </p>
                )}

                {lead?.id && (
                  <div className="mt-3">
                    <LeadFunnelActions
                      leadId={lead.id}
                      currentStatus={formData.status}
                      defaultActor={formData.owner || undefined}
                      onTransition={({ newStatus }) => {
                        setFormData(prev => ({ ...prev, status: newStatus as Lead['status'] }));
                      }}
                    />
                  </div>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Producto de Interés *
                </label>
                <select
                  multiple
                  value={formData.productInterest}
                  onChange={handleProductInterestChange}
                  className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                    errors.productInterest ? 'border-red-500 bg-red-50' : 'border-gray-300'
                  } min-h-[120px]`}
                  required
                >
                  {productOptions.map(option => (
                    <option key={option} value={option}>
                      {option}
                    </option>
                  ))}
                </select>
                <p className="text-xs text-gray-500 mt-1">
                  Mantén presionado Ctrl (Cmd en Mac) para seleccionar múltiples opciones
                </p>
                {errors.productInterest && (
                  <p className="text-red-600 text-xs mt-1">{errors.productInterest}</p>
                )}
              </div>
            </div>
          </div>

          {/* Lead Origin and Captured Data - Only shown when editing existing lead */}
          {lead?.id && (() => {
            const metadata = parseLeadFormData(lead);
            const hasFormData = metadata.realSource || metadata.projectDescription || metadata.budgetRange || metadata.estimatedTimeframe;

            if (!hasFormData) return null;

            return (
              <div className="bg-gradient-to-br from-blue-50 to-cyan-50 rounded-lg p-6 border border-blue-200">
                <div className="flex items-center space-x-2 mb-4">
                  <Info className="w-5 h-5 text-blue-600" />
                  <h3 className="text-lg font-medium text-gray-900">Origen del Lead</h3>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                  {/* Source */}
                  <div className="bg-white rounded-lg p-4 border border-blue-100">
                    <p className="text-xs font-medium text-gray-500 mb-2">Fuente de entrada</p>
                    <LeadSourceBadge source={metadata.realSource} size="md" />
                  </div>

                  {/* Qualification Status */}
                  {metadata.isQualified !== null && (
                    <div className="bg-white rounded-lg p-4 border border-blue-100">
                      <p className="text-xs font-medium text-gray-500 mb-2">Estado de calificación</p>
                      <LeadQualifiedBadge isQualified={metadata.isQualified} size="md" />
                    </div>
                  )}

                  {/* Created Date */}
                  {lead.createdAt && (
                    <div className="bg-white rounded-lg p-4 border border-blue-100">
                      <p className="text-xs font-medium text-gray-500 mb-2">Fecha de creación</p>
                      <div className="flex items-center space-x-2 text-sm text-gray-900">
                        <Calendar className="w-4 h-4 text-gray-400" />
                        <span>{new Date(lead.createdAt).toLocaleString('es-ES', {
                          day: '2-digit',
                          month: 'short',
                          year: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit'
                        })}</span>
                      </div>
                    </div>
                  )}
                </div>

                {/* Captured Project Data */}
                {(metadata.projectDescription || metadata.budgetRange || metadata.estimatedTimeframe || metadata.projectType) && (
                  <div className="mt-4">
                    <h4 className="text-sm font-semibold text-gray-700 mb-3 flex items-center space-x-2">
                      <FileText className="w-4 h-4" />
                      <span>Datos capturados del formulario</span>
                    </h4>
                    <div className="grid grid-cols-1 gap-3">
                      {metadata.projectType && (
                        <div className="bg-white rounded-lg p-3 border border-blue-100">
                          <p className="text-xs font-medium text-gray-500 mb-1">Tipo de proyecto</p>
                          <p className="text-sm text-gray-900">{metadata.projectType}</p>
                        </div>
                      )}

                      {metadata.projectDescription && (
                        <div className="bg-white rounded-lg p-3 border border-blue-100">
                          <p className="text-xs font-medium text-gray-500 mb-1">Descripción del proyecto</p>
                          <p className="text-sm text-gray-900 whitespace-pre-wrap">{metadata.projectDescription}</p>
                        </div>
                      )}

                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        {metadata.budgetRange && (
                          <div className="bg-white rounded-lg p-3 border border-blue-100">
                            <p className="text-xs font-medium text-gray-500 mb-1 flex items-center space-x-1">
                              <DollarSign className="w-3 h-3" />
                              <span>Presupuesto estimado</span>
                            </p>
                            <p className="text-sm font-medium text-gray-900">{metadata.budgetRange}</p>
                          </div>
                        )}

                        {metadata.estimatedTimeframe && (
                          <div className="bg-white rounded-lg p-3 border border-blue-100">
                            <p className="text-xs font-medium text-gray-500 mb-1 flex items-center space-x-1">
                              <Clock className="w-3 h-3" />
                              <span>Plazo estimado</span>
                            </p>
                            <p className="text-sm font-medium text-gray-900">{metadata.estimatedTimeframe}</p>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            );
          })()}

          {/* Lead Management */}
          <div>
            <h3 className="text-lg font-medium text-gray-900 mb-4">Gestión del Lead</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Responsable *
                </label>
                {loadingUsers ? (
                  <div className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-gray-50 flex items-center">
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600 mr-2"></div>
                    <span className="text-gray-500">Cargando usuarios...</span>
                  </div>
                ) : (
                  <select
                    value={formData.owner}
                    onChange={(e) => setFormData({ ...formData, owner: e.target.value })}
                    className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                      errors.owner ? 'border-red-500 bg-red-50' : 'border-gray-300'
                    }`}
                    required
                  >
                    <option value="">Seleccionar responsable</option>
                    {availableUsers.map(user => (
                      <option key={user.id} value={user.name}>
                        {user.name} ({user.role === 'superadmin' ? 'Admin' : 'Usuario'})
                      </option>
                    ))}
                  </select>
                )}
                {errors.owner && (
                  <p className="text-red-600 text-xs mt-1">{errors.owner}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Puntuación del Lead (0-100)
                </label>
                <div className="relative">
                  <input
                    type="number"
                    min="0"
                    max="100"
                    value={formData.score}
                    onChange={(e) => setFormData({ ...formData, score: parseInt(e.target.value) || 0 })}
                    className="w-full px-3 py-2 pr-10 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="0"
                  />
                  <Star className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                </div>
                <p className="text-xs text-gray-500 mt-1">
                  0-40: Bajo, 41-60: Medio, 61-80: Alto, 81-100: Muy Alto
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Último Contacto
                </label>
                <input
                  type="date"
                  value={formData.lastContactDate}
                  onChange={(e) => setFormData({ ...formData, lastContactDate: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Próximo Seguimiento
                </label>
                <input
                  type="date"
                  value={formData.nextFollowUp}
                  onChange={(e) => setFormData({ ...formData, nextFollowUp: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
            </div>
          </div>

          {/* Notes */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Notas del Lead
            </label>
            <textarea
              rows={4}
              value={formData.notes}
              onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="Información adicional sobre el lead, conversaciones, intereses específicos..."
            />
          </div>

          {/* Conversation context - Only shown when lead has conversation data */}
          {lead?.id && (lead?.conversationSummary || lead?.conversationFull) && (
            <div>
              <h3 className="text-lg font-medium text-gray-900 mb-3">Contexto de conversación</h3>

              {lead.conversationSummary && (
                <div className="mb-3">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Resumen de la conversación
                  </label>
                  <textarea
                    rows={4}
                    readOnly
                    value={lead.conversationSummary}
                    className="w-full px-3 py-2 border border-gray-200 rounded-lg bg-gray-50 text-gray-700 resize-none cursor-default"
                  />
                </div>
              )}

              {lead.conversationFull && (
                <div>
                  <button
                    type="button"
                    onClick={() => setConversationFullExpanded(prev => !prev)}
                    className="flex items-center gap-1 text-sm font-medium text-blue-600 hover:text-blue-800 mb-2"
                  >
                    {conversationFullExpanded ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                    {conversationFullExpanded ? 'Ocultar transcripción completa' : 'Ver transcripción completa'}
                  </button>
                  {conversationFullExpanded && (
                    <textarea
                      rows={10}
                      readOnly
                      value={lead.conversationFull}
                      className="w-full px-3 py-2 border border-gray-200 rounded-lg bg-gray-50 text-gray-700 text-sm font-mono resize-none cursor-default"
                    />
                  )}
                </div>
              )}
            </div>
          )}

          {/* Agent Calls Section - Only shown when editing existing lead */}
          {lead?.id && (
            <div>
              <AgentCallsList
                calls={agentCalls}
                isLoading={agentCallsLoading}
                title="Llamadas del asistente de Cero Ideas"
                onRowClick={(call) => {
                  setSelectedCall(call);
                  setCallModalOpen(true);
                }}
              />
            </div>
          )}

          {/* Lead Intelligence Panel - Only shown when editing existing lead */}
          {lead?.id && (
            <div>
              <h3 className="text-lg font-medium text-gray-900 mb-4">Análisis de Inteligencia</h3>
              <LeadIntelligencePanel lead={lead} />
            </div>
          )}

          {/* Lead Scoring Guide */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <h4 className="text-sm font-medium text-blue-900 mb-2">Guía de Puntuación de Leads</h4>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-xs">
              <div className="flex items-center space-x-2">
                <div className="w-3 h-3 bg-red-500 rounded-full"></div>
                <span className="text-blue-700">0-40: Bajo interés</span>
              </div>
              <div className="flex items-center space-x-2">
                <div className="w-3 h-3 bg-orange-500 rounded-full"></div>
                <span className="text-blue-700">41-60: Interés medio</span>
              </div>
              <div className="flex items-center space-x-2">
                <div className="w-3 h-3 bg-yellow-500 rounded-full"></div>
                <span className="text-blue-700">61-80: Alto interés</span>
              </div>
              <div className="flex items-center space-x-2">
                <div className="w-3 h-3 bg-green-500 rounded-full"></div>
                <span className="text-blue-700">81-100: Muy alto</span>
              </div>
            </div>
          </div>

          <div className="flex justify-end space-x-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
            >
              Cancelar
            </button>
            <button
              type="submit"
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              {lead ? 'Actualizar' : 'Crear'} Lead
            </button>
          </div>
        </form>
      </div>

      {/* Agent Call Detail Modal */}
      <AgentCallDetailModal
        call={selectedCall}
        open={callModalOpen}
        onClose={() => setCallModalOpen(false)}
      />
    </div>
  );
};

export default LeadModal;